import { clsx } from "clsx";
import { type VFC, useMemo } from "react";

import { CardRowSimple } from "widgets";

import styleTile from "@/modules/core/styles/Tile.module.scss";

import { SmartLink } from "../core/SmartLink.js";
import { type AttributeWidgetProps, AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { type IRenderableHit } from "./AlnPaginationModel.js";
import { type SequenceType } from "./SensorAlnInfoCard.js";
import { SensorPairwiseAln } from "./SensorPairwiseAln.js";
import { getHitDatabaseName } from "@/app-utils/blast.js";
import type { BlastHsp } from "@/graphql.js";
import { SEQUENCE_TYPE } from "@/settings/settings.js";

export interface ISensorHspProps {
  type: SequenceType;
  hsp: BlastHsp;
  hit: IRenderableHit;
  queryId: number;
  jobId: number;
}

/**
 * SensorHsp displays a BLAST high-scoring sequence pair of a BLAST hit.
 * The bounding box of the DOM node is used by SensorAlignmentList to sense, if it is currently visible.
 * Visible means that it is at least partially inside of the SensorAlignmentList viewport so is actually visible in the UI.
 * If SensorHsp is not visible, it is rendered with a low number of DOM nodes to determine its height.
 * Only visible SensorHSPs are rendered with one colored span/residue.
 * SensorHsp provides access to its outer DOM node and to its internal DOM nodes that represent the rows of the alignment.
 */
const SensorHsp: VFC<ISensorHspProps> = (props) => {
  const attributes = useMemo<AttributeWidgetProps[]>(() => {
    const hsp = props.hsp;
    const identity = hsp.ident || 0;
    const positive = hsp.pos || 0;
    const gaps = hsp.gaps || 0;
    const { jobId, queryId } = props;

    let hitRangeValue = (
      <span>
        {hsp.targetStart}-{hsp.targetStop}
      </span>
    );

    if (props.hit.target?.type?.name === SEQUENCE_TYPE.GENOMIC && props.hit.target?.organism?.id !== 0) {
      hitRangeValue = (
        <SmartLink
          tooltip="Show this HSP in genome view"
          forceTooltip
          to={`/job/${jobId}/results/${queryId}/hitdetails/${props.hit.rank}/genomeview/${hsp.rank}`}
        >
          {hsp.targetStart} – {hsp.targetStop}
        </SmartLink>
      );
    }

    const attributes: AttributeWidgetProps[] = [
      { label: "HSP", description: `${hsp.rank}`, forceWidth: 50 },
      { label: "Query range", description: `${hsp.queryStart}-${hsp.queryStop}`, forceWidth: 120 },
      { label: "Subject range", description: hitRangeValue, forceWidth: 140 },
      { label: "Score", description: `${hsp.score} bits (${hsp.rawScore})`, forceWidth: 140 },
      { label: "E-Value", description: `${hsp.eValue}`, forceWidth: 100 },
      { label: "Identities", description: `${identity}%`, forceWidth: 90 },
      { label: "Gaps", description: `${gaps}%`, forceWidth: 70 },
    ];

    if (hsp.pos) {
      attributes.push({ label: "Positives", description: `${positive}%`, forceWidth: 90 });
    }

    if (hsp.queryStrand) {
      attributes.push({ label: "Strand", description: `${hsp.queryStrand}/${hsp.targetStrand}` });
    }

    const db = getHitDatabaseName(props.hit);
    if (db) {
      attributes.push({ label: "Database", description: `${db}` });
    }

    const type = props.hit.target && props.hit.target.type ? props.hit.target.type : null;
    if (type) {
      attributes.push({ label: "Type", description: type.label });
    }

    const frame = props.hsp && props.hsp.frame ? props.hsp.frame : null;
    if (frame) {
      attributes.push({ label: "Frame", description: frame });
    }

    return attributes;
  }, [props]);

  return (
    <>
      <CardRowSimple>
        <div className={clsx(styleTile.tile, styleTile.tile_note)}>
          <AttributesListWidget attributes={attributes} wrap />
        </div>
      </CardRowSimple>
      <CardRowSimple>
        <SensorPairwiseAln hit={props.hit} hsp={props.hsp} type={props.type} />
      </CardRowSimple>
    </>
  );
};

export { SensorHsp };
